import { create } from "zustand";
import { ChatMessage, SmartiePlan } from "../types";
import { v4 as uuidv4 } from "uuid";
import {
  fetchChatHistory,
  fetchChatSessionSummaries,
  ChatSessionSummary,
  PaginatedChatSessionSummaryResponse,
} from "../services/chatService";
import { streamChat, analyzeUrl, updateSession, deleteSession } from "../services/apiClientV2";
import { AppError, toAppError } from "../types/errors";
import { refreshCurrentUserUsage } from "../services/authService";
import useAuthStore from "../store/authStore";
import { useToastStore } from "../store/toastStore";
import * as Sentry from "@sentry/react";
import { logDebug, logError, logWarn } from "../utils/logging"; // Import logging utilities
import { extractFirstUrl } from "../utils/urlUtils";
import { trackEvent } from "../services/analyticsService";
// NOTE: Avoid static import of guidedFlowStore here to prevent circular dependency.

interface ChatState {
  sessionId: string;
  messages: ChatMessage[];
  currentAIMessage: ChatMessage | null; // Holds the AI message being streamed
  // currentStreamReader: ReadableStreamDefaultReader<Uint8Array> | null; // To manage the stream reader
  currentStreamController: AbortController | null; // To manage the stream cancellation
  isStreaming: boolean;
  isAnalyzingUrl: boolean;
  generalError: string | null; // For general errors
  streamError: string | null; // For errors during streaming
  isLoadingHistory: boolean;
  historyError: string | null;
  totalMessages: number;
  currentOffset: number;
  messagesLimit: number;

  // New state for session summaries
  chatHistorySessions: ChatSessionSummary[];
  isLoadingHistorySessions: boolean;
  historySessionsError: string | null;
  sessionSummariesOffset: number;
  totalSessionSummaries: number;
  sessionSummariesLimit: number; // To control pagination for summaries
  hasMoreSessionSummaries: boolean;
  selectedSessionIdForView: string | null; // ID of the session whose messages are currently being viewed

  stopStreaming: () => void;

  setSessionId: (sessionId: string) => void;
  startStreaming: (
    initialUserMessage: ChatMessage,
    displayPromptInUI?: boolean,
  ) => void;
  appendStreamChunk: (chunk: string) => void;
  finalizeStreaming: (finalData?: { messageId?: string }) => void;
  setGeneralError: (error: string | null) => void;
  setStreamingError: (errorMsg: string) => void;
  // setCurrentStreamReader: (reader: ReadableStreamDefaultReader<Uint8Array> | null) => void;
  setCurrentStreamController: (controller: AbortController | null) => void;
  fetchAndLoadHistory: (sessionIdToLoad?: string) => Promise<void>;
  initializeSession: () => void;
  sendMessage: (
    messageContent: string,
    currentPageUrl?: string,
  ) => Promise<void>;

  // New actions for session summaries
  fetchSessionSummaries: (loadMore?: boolean) => Promise<void>;
  setSelectedSessionIdForView: (sessionId: string | null) => void;
  startNewChatSession: () => void;
  renameChatSession: (sessionId: string, newTitle: string) => Promise<void>;
  deleteChatSession: (sessionId: string) => Promise<void>;

  sendMessageWithMindspace: (
    finalPrompt: string,
    mindspaceId: string,
    templateId?: string,
    contextOverride?: Record<string, string>,
    displayContent?: string,
  ) => Promise<void>;
}

// Helper function to get/set sessionId from sessionStorage
const SESSION_ID_KEY = "smartie-sessionId";

const initializeSessionId = (): string => {
  let storedSessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!storedSessionId) {
    storedSessionId = uuidv4();
    sessionStorage.setItem(SESSION_ID_KEY, storedSessionId);
    logDebug("New session ID generated:", storedSessionId);
  } else {
    logDebug("Existing session ID found:", storedSessionId);
  }
  return storedSessionId;
};

export const useChatStore = create<ChatState>((set, get) => ({
  sessionId: initializeSessionId(),
  messages: [],
  currentAIMessage: null,
  // currentStreamReader: null,
  currentStreamController: null,
  isStreaming: false,
  isAnalyzingUrl: false,
  generalError: null,
  streamError: null,
  isLoadingHistory: false,
  historyError: null,
  totalMessages: 0,
  currentOffset: 0,
  messagesLimit: 30,

  // Initial state for session summaries
  chatHistorySessions: [],
  isLoadingHistorySessions: false,
  historySessionsError: null,
  sessionSummariesOffset: 0,
  totalSessionSummaries: 0,
  sessionSummariesLimit: 15, // Default limit for session summaries page
  hasMoreSessionSummaries: true, // Assume more initially until first fetch
  selectedSessionIdForView: null,

  stopStreaming: () => {
    const { currentStreamController, isStreaming } = get();
    if (isStreaming && currentStreamController) {
      logDebug("[chatStore] User manually stopped streaming.");
      currentStreamController.abort();

      // Finalize current state with what we have so far
      set((state) => {
        if (!state.currentAIMessage) return {
          isStreaming: false,
          currentStreamController: null
        };

        const finalAIMessage: ChatMessage = {
          ...state.currentAIMessage,
          content: state.currentAIMessage.content + "\n\n_(Generare oprită de utilizator)_",
          isLoading: false,
        };

        return {
          messages: [...state.messages, finalAIMessage],
          currentAIMessage: null,
          isStreaming: false,
          currentStreamController: null,
          streamError: null,
        };
      });
    }
  },

  setSessionId: (sessionId) => {
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    set({ sessionId });
  },

  startStreaming: (initialUserMessage, displayPromptInUI = true) => {
    const userMessageWithMeta: ChatMessage = {
      id: initialUserMessage.id || uuidv4(),
      created_at: initialUserMessage.created_at || new Date().toISOString(),
      ...initialUserMessage,
    };
    set((state) => ({
      messages: displayPromptInUI
        ? [...state.messages, userMessageWithMeta]
        : state.messages,
      isStreaming: true,
      currentAIMessage: {
        id: uuidv4(), // Temporary ID until backend confirms
        sender: "ai",
        content: "",
        isLoading: true,
        isThinking: false,
        plan: undefined,
        created_at: new Date().toISOString(),
      },
      generalError: null,
      streamError: null,
      historyError: null,
    }));
  },

  appendStreamChunk: (chunk) => {
    // UMW-Debug: Log only the size of the chunk to avoid spamming the console with huge strings
    // logDebug(`[chatStore] appendStreamChunk received chunk of length: ${chunk.length}`);
    set((state) => {
      if (!state.currentAIMessage) {
        // Only log warning if strictly necessary, or throttle it
        // logWarn("[chatStore] appendStreamChunk: currentAIMessage is null, cannot append.");
        return {};
      }

      const newContent = state.currentAIMessage.content + chunk;

      return {
        currentAIMessage: {
          ...state.currentAIMessage,
          content: newContent,
          isThinking: false, // Failsafe: ensure thinking stops when tokens arrive
        },
      };
    });
  },

  finalizeStreaming: (finalData) => {
    set((state) => {
      if (!state.currentAIMessage)
        return {
          isStreaming: false,
          // currentStreamReader: null
          currentStreamController: null, // Clear controller on finalize
        };

      // Update message ID if provided by backend
      const messageId = finalData?.messageId || state.currentAIMessage.id;

      const finalAIMessage: ChatMessage = {
        ...state.currentAIMessage,
        id: messageId,
        isLoading: false,
      };
      // Call refreshCurrentUserUsage after successfully finalizing the stream
      logDebug(
        "[ChatStore] FinalizeStreaming: Attempting to call refreshCurrentUserUsage.",
      );
      refreshCurrentUserUsage().catch((error) => {
        logError(
          "[ChatStore] Error refreshing user usage after stream finalization:",
          error,
        );
        // Decide if any specific error handling in chatStore is needed here
      });
      return {
        messages: [...state.messages, finalAIMessage],
        currentAIMessage: null,
        isStreaming: false,
        // currentStreamReader: null,
        currentStreamController: null, // Clear controller on finalize
        streamError: null,
      };
    });
  },

  setGeneralError: (errorMsg) =>
    set({
      generalError:
        typeof errorMsg === "string"
          ? errorMsg
          : errorMsg
            ? JSON.stringify(errorMsg)
            : null,
      isStreaming: false,
      currentAIMessage: null,
      // currentStreamReader: null,
      currentStreamController: null, // Clear controller on general error
    }),

  setStreamingError: (errorMsg) => {
    set((state) => {
      let finalMessages = state.messages;
      if (state.currentAIMessage) {
        const erroredAIMessage: ChatMessage = {
          ...state.currentAIMessage,
          content:
            state.currentAIMessage.content + `\n[Eroare: Flux întrerupt]`,
          isLoading: false,
        };
        finalMessages = [...state.messages, erroredAIMessage];
      }
      return {
        messages: finalMessages,
        streamError:
          typeof errorMsg === "string"
            ? errorMsg
            : errorMsg
              ? JSON.stringify(errorMsg)
              : "Eroare necunoscută",
        isStreaming: false,
        currentAIMessage: null,
        // currentStreamReader: null,
        currentStreamController: null, // Clear controller on stream error
      };
    });
  },

  // setCurrentStreamReader: (reader) => set({ currentStreamReader: reader }),
  setCurrentStreamController: (controller) =>
    set({ currentStreamController: controller }),

  fetchAndLoadHistory: async (sessionIdToLoad?: string) => {
    const {
      sessionId: currentActiveSessionId,
      messagesLimit,
      selectedSessionIdForView,
    } = get();
    const targetSessionId =
      sessionIdToLoad || selectedSessionIdForView || currentActiveSessionId;

    if (!targetSessionId) {
      set({
        messages: [],
        totalMessages: 0,
        currentOffset: 0,
        isLoadingHistory: false,
        historyError: "Niciun ID de sesiune pentru a încărca istoricul.",
      });
      return;
    }

    set({ isLoadingHistory: true, historyError: null });

    try {
      const initialOffset = 0; // Always fetch from the beginning for a selected session's messages
      const historyData = await fetchChatHistory(
        targetSessionId,
        messagesLimit,
        initialOffset,
      );
      const enrichedMessages = historyData.messages
        .filter((m) => !m.message_metadata?.hidden) // Filter out hidden messages
        .map((m) => ({
          ...m,
          id: m.id || uuidv4(),
          // Map displayContent from metadata if present
          displayContent:
            (m.message_metadata?.displayContent as string) || m.displayContent,
          // Map plan from metadata if present
          plan: (m.message_metadata?.plan as SmartiePlan) || undefined,
        }));

      set({
        messages: enrichedMessages,
        totalMessages: historyData.totalMessages,
        currentOffset: initialOffset, // Reset offset for messages of this session
        isLoadingHistory: false,
        // If we are loading a specific session for view, it might not be the active `sessionId` for new messages
        // For now, `fetchAndLoadHistory` just loads messages into the view.
        // `selectedSessionIdForView` should be set by the component triggering this.
      });
      logDebug(`History loaded for session ${targetSessionId}:`, historyData);
    } catch (err) {
      logError("Error loading history:", err);
      const appErr = err as AppError;

      // Treat 404 as empty history (covers both "No history found" and backend "Session not found")
      if (appErr && (appErr as AppError).status === 404) {
        set({
          messages: [],
          totalMessages: 0,
          currentOffset: 0,
          isLoadingHistory: false,
          historyError: null,
        });
        logDebug("History 404 treated as empty state for session.");
      } else {
        const errorMessage =
          appErr.detail ||
          appErr.message ||
          (err instanceof Error
            ? err.message
            : "Eroare la încărcarea istoricului");
        set({ historyError: errorMessage, isLoadingHistory: false });
      }
    }
  },

  initializeSession: () => {
    // This function can be called on initial widget load or when a new session is needed.
    // For now, it mainly ensures we have a valid session ID.
    // If we need to pre-fetch user data or perform other init tasks, they can go here.
    const currentSessionId = get().sessionId;
    if (!currentSessionId) {
      const newId = initializeSessionId(); // This gets from sessionStorage or creates new
      set({ sessionId: newId });
    }
    logDebug(`Session initialized with ID: ${get().sessionId}`);
    // Potentially pre-fetch history summaries here if widget is opened by default
    // get().fetchSessionSummaries();
  },

  sendMessage: async (messageContent: string, currentPageUrl?: string) => {
    const { sessionId, isStreaming, currentStreamController } = get();
    void currentPageUrl;

    // --- Analytics Tracking for Message Sent ---
    const { userStatus } = useAuthStore.getState();
    if (userStatus && userStatus !== "unknown") {
      trackEvent("message_sent", { user_status: userStatus });
    }
    // --- End Analytics Tracking ---

    // Prevent sending a new message while an AI response is being streamed.
    if (isStreaming) {
      logWarn("sendMessage: Blocked, currently streaming.");
      // Abort the previous stream if a new message is sent.
      if (currentStreamController) {
        currentStreamController.abort();
        set({ currentStreamController: null }); // Clear it after aborting
      }
      return;
    }

    const {
      selectedSessionIdForView,
      startStreaming,
      setGeneralError,
      setSessionId,
    } = get();

    // Determine the target session to send the message to
    const targetSessionId = selectedSessionIdForView || sessionId;

    // If user is viewing a different session, align the active session to the viewed one
    if (selectedSessionIdForView && selectedSessionIdForView !== sessionId) {
      logDebug(
        "sendMessage: Switching active sessionId to selected session for sending.",
      );
      setSessionId(selectedSessionIdForView);
    }

    if (!targetSessionId) {
      setGeneralError(
        "ID-ul sesiunii nu este disponibil. Mesajul nu poate fi trimis.",
      );
      return;
    }

    const userMessage: ChatMessage = {
      sender: "user",
      content: messageContent,
    };

    // Abort any ongoing stream before sending a new message
    const { currentStreamController: existingController } = get();
    if (existingController) {
      logDebug(
        "sendMessage: Aborting existing stream before sending new message.",
      );
      existingController.abort();
      set({ currentStreamController: null }); // Clear it after aborting
    }

    startStreaming(userMessage);

    // Check for URL
    let urlContent: string | undefined = undefined;
    let urlFetchStatus: import("../services/apiClientV2").UrlFetchStatus | undefined;
    let attemptedUrl: string | undefined = undefined;
    let urlFetchError: string | undefined = undefined;
    const urlToAnalyze = extractFirstUrl(messageContent);

    if (urlToAnalyze) {
      attemptedUrl = urlToAnalyze;
      set({ isAnalyzingUrl: true });
      try {
        const result = await analyzeUrl(urlToAnalyze);
        urlContent = result.content;

        if (!urlContent || urlContent.trim().length === 0) {
          urlFetchStatus = "empty";
          useToastStore.getState().addToast({
            message: "URL-ul a fost accesat dar nu conține text vizibil.",
            type: "warning",
          });
        } else {
          urlFetchStatus = "succeeded";
          useToastStore.getState().addToast({
            message: "Conținut URL adăugat la context",
            type: "success",
          });
        }
      } catch (e) {
        logError("Failed to analyze URL", e);
        urlFetchStatus = "failed";
        urlFetchError = (e instanceof Error ? e.message : String(e)).slice(0, 2000);
        useToastStore.getState().addToast({
          message: "N-am putut citi link-ul, continui fără el.",
          type: "warning",
        });
        // Proceed without URL content
      } finally {
        set({ isAnalyzingUrl: false });
      }
    }

    // Get active mindspace ID
    let mindspaceId: string | undefined;
    try {
      const { useMindspaceStore } = await import("./mindspaceStore");
      mindspaceId = useMindspaceStore.getState().activeMindspaceId || undefined;
    } catch (e) {
      logWarn("Could not load mindspaceStore in chatStore", e);
    }

    const unifiedRequest: import("../services/apiClientV2").ChatStreamUnifiedRequest =
    {
      session_id: targetSessionId,
      message: messageContent,
      url_content: urlContent,
      url_fetch_status: urlFetchStatus,
      attempted_url: attemptedUrl,
      url_fetch_error: urlFetchError,
      mindspace_id: mindspaceId,
      // Note: currentPageUrl is not part of v2 unified schema; omit or include via context if needed
    };

    try {
      await streamChat(
        unifiedRequest,
        (chunk: string) => get().appendStreamChunk(chunk),
        (data?: { messageId?: string }) => get().finalizeStreaming(data),
        (appError: AppError) => {
          logError(
            `[chatStore] sendMessage - onError callback received:`,
            appError,
          );
          if (import.meta.env.PROD) {
            Sentry.captureException(new Error(appError.message), {
              tags: {
                store: "chatStore",
                action: "sendMessage",
                event: "streamError",
              },
              extra: { appError },
            });
          }
          const errorDetail = appError.detail || appError.message;
          get().setStreamingError(errorDetail);
          if (appError.status === 401 || appError.status === 403) {
            useAuthStore.getState().setAuthError(errorDetail);
          } else if (appError.status === 429) {
            useAuthStore.getState().setRateLimitError(true, errorDetail);
          }
        },
        (controller: AbortController | null) =>
          get().setCurrentStreamController(controller),
        (status: string) => {
          if (status === "thinking") {
            set((state) => ({
              currentAIMessage: state.currentAIMessage
                ? { ...state.currentAIMessage, isThinking: true }
                : null,
            }));
          }
        },
        (plan: SmartiePlan) => {
          set((state) => ({
            currentAIMessage: state.currentAIMessage
              ? { ...state.currentAIMessage, isThinking: false, plan }
              : null,
          }));
        },
      );
    } catch (err) {
      logError("Error in sendMessage calling streamChatResponse:", err);
      const appErr = toAppError(err, 500);
      const errorMessage = appErr.detail || appErr.message;
      get().setStreamingError(errorMessage);
    }
  },

  // --- New actions for session summaries ---
  fetchSessionSummaries: async (loadMore = false) => {
    const {
      sessionSummariesLimit,
      sessionSummariesOffset,
      chatHistorySessions,
    } = get();

    // Import mindspaceStore dynamically or use existing import if safe (it is imported as type, but we need value)
    // To avoid circular dependency issues if any, we use inline import or assume it's safe.
    // However, `useMindspaceStore` is NOT imported at top level. Let's import it.
    // Wait, I need to add import at top of file first?
    // Or just use import inside function.

    let mindspaceId: string | undefined;
    try {
      const { useMindspaceStore } = await import("./mindspaceStore");
      mindspaceId = useMindspaceStore.getState().activeMindspaceId || undefined;
    } catch (e) {
      logWarn("Could not load mindspaceStore in chatStore", e);
    }

    set({ isLoadingHistorySessions: true, historySessionsError: null });

    const offsetToFetch = loadMore ? sessionSummariesOffset : 0;

    try {
      const summaryData: PaginatedChatSessionSummaryResponse =
        await fetchChatSessionSummaries(sessionSummariesLimit, offsetToFetch, mindspaceId);

      const newSummaries = summaryData.sessions;
      const newOffset = offsetToFetch + newSummaries.length;
      const allSummaries = loadMore
        ? [...chatHistorySessions, ...newSummaries]
        : newSummaries;

      set({
        chatHistorySessions: allSummaries,
        totalSessionSummaries: summaryData.total_sessions,
        sessionSummariesOffset: newOffset,
        hasMoreSessionSummaries: newOffset < summaryData.total_sessions,
        isLoadingHistorySessions: false,
      });
      logDebug("Session summaries loaded:", summaryData);
    } catch (err) {
      logError("Error loading session summaries:", err);
      const appErr = err as AppError;
      const errorMessage =
        appErr.detail ||
        appErr.message ||
        (err instanceof Error
          ? err.message
          : "Eroare la încărcarea sumarelor sesiunilor");
      set({
        historySessionsError: errorMessage,
        isLoadingHistorySessions: false,
      });
    }
  },

  setSelectedSessionIdForView: (sessionIdToView: string | null) => {
    const { fetchAndLoadHistory } = get(); // Only fetchAndLoadHistory is needed here
    set({ selectedSessionIdForView: sessionIdToView });
    if (sessionIdToView) {
      // If a session is selected for viewing, load its history.
      // This does NOT change the active `sessionId` for sending new messages unless `startNewChatSession` is called.
      fetchAndLoadHistory(sessionIdToView);
    } else {
      // If null, it implies user wants to clear the view or prepare for a new chat.
      // The active `sessionId` is still the one for new messages.
      // We might want to clear messages here, or `fetchAndLoadHistory` handles it with null.
      set({ messages: [], totalMessages: 0, currentOffset: 0 }); // Clear messages if null
    }
  },

  startNewChatSession: () => {
    const newSessionId = uuidv4();
    get().setSessionId(newSessionId); // This also updates sessionStorage
    set({
      messages: [],
      totalMessages: 0,
      currentOffset: 0,
      historyError: null,
      streamError: null,
      generalError: null,
      currentAIMessage: null,
      isStreaming: false,
      selectedSessionIdForView: newSessionId,
    });

    // NEW: Ensure guided flow state is fully reset so that WelcomeScreen shows
    try {
      // Dynamic import to avoid circular dependency
      import("./guidedFlowStore")
        .then((mod) => {
          mod.useGuidedFlowStore.getState().resetFlow();
        })
        .catch((errImport) => {
          if (process.env.NODE_ENV !== "production") {
            console.error(
              "[chatStore] Dynamic import of guidedFlowStore failed",
              errImport,
            );
          }
        });
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error(
          "[chatStore] Failed to reset guidedFlowStore during new chat",
          err,
        );
      }
    }

    logDebug("Started new chat session:", newSessionId);
  },

  renameChatSession: async (sessionId: string, newTitle: string) => {
    try {
      await updateSession(sessionId, { title: newTitle });

      set((state) => ({
        chatHistorySessions: state.chatHistorySessions.map((s) =>
          s.session_id === sessionId ? { ...s, title: newTitle } : s
        ),
      }));

      useToastStore.getState().addToast({
        message: "Sesiunea a fost redenumită.",
        type: "success",
      });
    } catch (err) {
      logError("Error renaming session:", err);
      useToastStore.getState().addToast({
        message: "Eroare la redenumirea sesiunii.",
        type: "error",
      });
    }
  },

  deleteChatSession: async (sessionId: string) => {
    try {
      await deleteSession(sessionId);

      const { sessionId: currentSessionId, selectedSessionIdForView } = get();

      set((state) => ({
        chatHistorySessions: state.chatHistorySessions.filter(
          (s) => s.session_id !== sessionId
        ),
        totalSessionSummaries: Math.max(0, state.totalSessionSummaries - 1),
      }));

      // If deleted session was active or viewed, start a new one
      if (sessionId === currentSessionId || sessionId === selectedSessionIdForView) {
        get().startNewChatSession();
        useToastStore.getState().addToast({
          message: "Sesiunea curentă a fost ștearsă.",
          type: "info",
        });
      } else {
        useToastStore.getState().addToast({
          message: "Sesiunea a fost ștearsă.",
          type: "success",
        });
      }

    } catch (err) {
      logError("Error deleting session:", err);
      useToastStore.getState().addToast({
        message: "Eroare la ștergerea sesiunii.",
        type: "error",
      });
    }
  },

  sendMessageWithMindspace: async (
    finalPrompt: string,
    mindspaceId: string,
    templateId?: string,
    contextOverride?: Record<string, string>,
    displayContent?: string,
  ) => {
    const {
      sessionId,
      startStreaming,
      setGeneralError,
      selectedSessionIdForView,
      setSessionId,
    } = get();

    const targetSessionId = selectedSessionIdForView || sessionId;

    if (selectedSessionIdForView && selectedSessionIdForView !== sessionId) {
      setSessionId(selectedSessionIdForView);
    }

    if (!targetSessionId) {
      setGeneralError(
        "ID-ul sesiunii nu este disponibil. Mesajul nu poate fi trimis.",
      );
      return;
    }

    const userMessage: ChatMessage = {
      sender: "user",
      content: finalPrompt,
      displayContent: displayContent,
    };

    // Abort any ongoing stream before sending a new message
    const { currentStreamController: existingController } = get();
    if (existingController) {
      existingController.abort();
      set({ currentStreamController: null });
    }

    // Hide the user message from UI if it's a template-based system prompt (Task F2.5)
    // The user wants to see only the AI response for these predefined actions.
    const displayPromptInUI = !templateId;
    startStreaming(userMessage, displayPromptInUI);

    // Construct metadata for persistence
    const messageMetadata: Record<string, unknown> = {};
    if (displayContent) {
      messageMetadata.displayContent = displayContent;
    }
    // If hidden from UI, mark as hidden in history too
    if (!displayPromptInUI) {
      messageMetadata.hidden = true;
    }

    // Check for URL
    let urlContent: string | undefined = undefined;
    let urlFetchStatus: import("../services/apiClientV2").UrlFetchStatus | undefined;
    let attemptedUrl: string | undefined = undefined;
    let urlFetchError: string | undefined = undefined;
    const urlToAnalyze = extractFirstUrl(finalPrompt);

    if (urlToAnalyze) {
      attemptedUrl = urlToAnalyze;
      set({ isAnalyzingUrl: true });
      try {
        const result = await analyzeUrl(urlToAnalyze);
        urlContent = result.content;

        if (!urlContent || urlContent.trim().length === 0) {
          urlFetchStatus = "empty";
          useToastStore.getState().addToast({
            message: "URL-ul a fost accesat dar nu conține text vizibil.",
            type: "warning",
          });
        } else {
          urlFetchStatus = "succeeded";
          useToastStore.getState().addToast({
            message: "Conținut URL adăugat la context",
            type: "success",
          });
        }
      } catch (e) {
        logError("Failed to analyze URL in mindspace message", e);
        urlFetchStatus = "failed";
        urlFetchError = (e instanceof Error ? e.message : String(e)).slice(0, 2000);
        useToastStore.getState().addToast({
          message: "N-am putut citi link-ul, continui fără el.",
          type: "warning",
        });
      } finally {
        set({ isAnalyzingUrl: false });
      }
    }

    const isGuestUser = useAuthStore.getState().isGuest;

    if (!isGuestUser && !mindspaceId) {
      setGeneralError(
        "Mindspace-ul nu este disponibil. Nu se poate trimite mesajul.",
      );
      return;
    }

    const unifiedRequest: import("../services/apiClientV2").ChatStreamUnifiedRequest =
    {
      session_id: targetSessionId,
      message: finalPrompt,
      template_id: templateId,
      context_override: contextOverride,
      url_content: urlContent,
      url_fetch_status: urlFetchStatus,
      attempted_url: attemptedUrl,
      url_fetch_error: urlFetchError,
      message_metadata:
        Object.keys(messageMetadata).length > 0 ? messageMetadata : undefined,
    };
    if (!isGuestUser) {
      unifiedRequest.mindspace_id = mindspaceId;
    } else if (contextOverride) {
      // For guests, pass canonical context collected during guided flow as session_context
      unifiedRequest.session_context = contextOverride;
    }

    try {
      await streamChat(
        unifiedRequest,
        (chunk: string) => get().appendStreamChunk(chunk),
        (data?: { messageId?: string }) => get().finalizeStreaming(data),
        (appErr: AppError) => {
          logError("stream error unified", appErr);
          get().setStreamingError(appErr.detail || appErr.message);
        },
        (controller: AbortController | null) =>
          get().setCurrentStreamController(controller),
        (status: string) => {
          if (status === "thinking") {
            set((state) => ({
              currentAIMessage: state.currentAIMessage
                ? { ...state.currentAIMessage, isThinking: true }
                : null,
            }));
          }
        },
        (plan: SmartiePlan) => {
          set((state) => ({
            currentAIMessage: state.currentAIMessage
              ? { ...state.currentAIMessage, isThinking: false, plan }
              : null,
          }));
        },
      );
    } catch (err) {
      logError("Error in sendMessageWithMindspace (unified)", err);
      const appErr = toAppError(err, 500);
      get().setStreamingError(appErr.detail || appErr.message);
    }
  },
}));
